/**
 * @file pages/CustomerDetailPage.js
 * @description Customer Butler — the customer record. Header + tabs: Overview
 * (core + custom fields, editable), Properties (with service history), Contacts,
 * Timeline (every interaction in one stream), and Consent (provable opt-ins).
 * Custom fields render dynamically from the company's FieldDefinitions (ADR-8).
 */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { Modal } from '../design-system';
import { apiGet, apiSend } from '../lib/api';
import { CustomFieldsForm, CustomFieldsDisplay } from '../components/CustomFieldsForm';
import { TagEditor, TagChips, useTagSuggestions } from '../components/TagEditor';
import { OfficeNotesPanel } from '../components/OfficeNotesPanel';
// WHY (2026-10-14, Honest UI, James via Fable): the classic look offers only
// what the person's Customer Butler level (HR Team page) lets the server do.
import { useCustomerAccessView } from './customer/customerAccessView';

const TABS = ['Overview', 'Properties', 'Contacts', 'Timeline', 'Consent'];
const CHANNELS = ['call', 'email', 'sms', 'meeting', 'note'];
const CONSENT_TYPES = ['marketing_email', 'marketing_sms', 'call', 'data_processing'];

function CustomerDetailPage() {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();

  const [customer, setCustomer] = useState(null);
  const [defsCustomer, setDefsCustomer] = useState([]);
  const [defsProperty, setDefsProperty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Overview');
  const [modal, setModal] = useState(null); // 'edit' | 'property' | 'contact' | 'comm' | 'consent'
  const tagSuggestions = useTagSuggestions();
  const [savingTags, setSavingTags] = useState(false);
  const accessView = useCustomerAccessView(); // Look up = read only; take-off = Manager

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cust, dc, dp] = await Promise.all([
        apiGet(`/customer/customers/${id}`),
        apiGet('/customer/field-definitions?entityType=Customer&activeOnly=true').catch(() => []),
        apiGet('/customer/field-definitions?entityType=Property&activeOnly=true').catch(() => []),
      ]);
      setCustomer(cust);
      setDefsCustomer(dc);
      setDefsProperty(dp);
    } catch (e) {
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  /**
   * Save tags the moment a chip is added or removed.
   *
   * Tagging is a one-second action - making someone open the Edit modal and
   * press Save would cost more than the tag is worth. The chip is applied
   * optimistically so the UI never feels laggy; if the PUT fails we reload the
   * record so the screen tells the truth rather than the wish.
   *
   * @param {string[]} next - the complete new tag list
   */
  const saveTags = useCallback(async (next) => {
    setCustomer((c) => (c ? { ...c, tags: next } : c));
    setSavingTags(true);
    const r = await apiSend(`/customer/customers/${id}`, 'PUT', { tags: next });
    setSavingTags(false);
    if (!r.ok) load();
  }, [id, load]);

  if (!auth.isAuthenticated) return <Navigate to="/login" />;
  if (loading) return <div className="page-content" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary,#666)' }}>Loading…</div>;
  if (!customer) return (
    <div className="page-content" style={{ padding: '2rem', textAlign: 'center' }}>
      <h3>Customer not found</h3>
      <button style={S.btnSecondary} onClick={() => navigate('/customers')}>← Back to Customers</button>
    </div>
  );

  return (
    <div className="page-content" style={{ padding: '1rem', maxWidth: '1000px', margin: '0 auto' }}>
      <button style={S.back} onClick={() => navigate('/customers')}>← Customers</button>

      <div style={S.headerCard}>
        <div>
          <h2 style={{ margin: 0 }}>{customer.displayName}</h2>
          <div style={S.subline}>
            {customer.type === 'company' ? 'Company' : 'Person'} · <span style={{ textTransform: 'capitalize' }}>{customer.status}</span>
            {customer.primaryEmail && <> · {customer.primaryEmail}</>}
            {customer.primaryPhone && <> · {customer.primaryPhone}</>}
          </div>
          <TagChips tags={customer.tags} />
        </div>
        {accessView.mayWrite ? <button style={S.btnSecondary} onClick={() => setModal('edit')}>Edit</button> : null}
      </div>

      <div style={S.tabs}>
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{ ...S.tab, ...(tab === t ? S.tabActive : {}) }}>
            {t}
            {t === 'Properties' && customer.properties ? ` (${customer.properties.length})` : ''}
            {t === 'Contacts' && customer.contacts ? ` (${customer.contacts.length})` : ''}
          </button>
        ))}
      </div>

      <div style={S.panel}>
        {tab === 'Overview' && (
          <>
            <Section title="Details">
              <Grid>
                <KV label="Type" value={customer.type} />
                <KV label="Status" value={customer.status} />
                <KV label="Email" value={customer.primaryEmail} />
                <KV label="Phone" value={customer.primaryPhone} />
                <KV label="Source" value={customer.source} />
              </Grid>
            </Section>
            <Section title="Tags">
              {accessView.mayWrite ? (
                <TagEditor
                  tags={customer.tags || []}
                  suggestions={tagSuggestions}
                  disabled={savingTags}
                  onChange={saveTags}
                />
              ) : (
                <TagChips tags={customer.tags} />
              )}
            </Section>
            {defsCustomer.length > 0 && (
              <Section title="Custom Fields">
                <CustomFieldsDisplay definitions={defsCustomer} values={customer.customFields || {}} />
              </Section>
            )}
            <OfficeNotesPanel customerId={id} mayAdd={accessView.mayWrite} mayTakeOff={accessView.mayTakeOff} />
          </>
        )}

        {tab === 'Properties' && (
          <TabList
            items={customer.properties || []}
            emptyText="No properties yet."
            addLabel="+ Add Property"
            onAdd={accessView.mayWrite ? () => setModal('property') : null}
            renderItem={(p) => <PropertyRow key={p.id} property={p} defs={defsProperty} />}
          />
        )}

        {tab === 'Contacts' && (
          <TabList
            items={customer.contacts || []}
            emptyText="No contacts yet."
            addLabel="+ Add Contact"
            onAdd={accessView.mayWrite ? () => setModal('contact') : null}
            renderItem={(c) => (
              <div key={c.id} style={S.itemCard}>
                <strong>{[c.firstName, c.lastName].filter(Boolean).join(' ') || '(no name)'}</strong>
                {c.isPrimary && <span style={S.pill}>Primary</span>}
                <TagChips tags={c.tags} max={4} />
                <div style={S.itemSub}>{c.role || ''} {c.email ? `· ${c.email}` : ''} {c.phone ? `· ${c.phone}` : ''}</div>
              </div>
            )}
          />
        )}

        {tab === 'Timeline' && <TimelineTab customerId={id} onLog={accessView.mayWrite ? () => setModal('comm') : null} />}
        {tab === 'Consent' && <ConsentTab customerId={id} onAdd={accessView.mayWrite ? () => setModal('consent') : null} />}
      </div>

      {/* Modals */}
      <Modal isOpen={modal === 'edit'} onClose={() => setModal(null)} title="Edit Customer">
        <EditCustomerForm customer={customer} defs={defsCustomer} onCancel={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      </Modal>
      <Modal isOpen={modal === 'property'} onClose={() => setModal(null)} title="Add Property">
        <PropertyForm customerId={id} defs={defsProperty} onCancel={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      </Modal>
      <Modal isOpen={modal === 'contact'} onClose={() => setModal(null)} title="Add Contact">
        <ContactForm customerId={id} onCancel={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      </Modal>
      <Modal isOpen={modal === 'comm'} onClose={() => setModal(null)} title="Log Interaction">
        <CommForm customerId={id} onCancel={() => setModal(null)} onSaved={() => { setModal(null); setTab('Timeline'); }} />
      </Modal>
      <Modal isOpen={modal === 'consent'} onClose={() => setModal(null)} title="Record Consent">
        <ConsentForm customerId={id} onCancel={() => setModal(null)} onSaved={() => { setModal(null); setTab('Consent'); }} />
      </Modal>
    </div>
  );
}

/* ---------- small presentational helpers ---------- */
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={S.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem' }}>{children}</div>; }
function KV({ label, value }) {
  if (!value) return null;
  return <div><div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary,#9CA3AF)' }}>{label}</div><div style={{ fontSize: '0.875rem', textTransform: label === 'Type' || label === 'Status' ? 'capitalize' : 'none' }}>{value}</div></div>;
}
function TabList({ items, emptyText, addLabel, onAdd, renderItem }) {
  return (
    <>
      {onAdd ? (
        <div style={{ textAlign: 'right', marginBottom: '0.75rem' }}>
          <button style={S.btnPrimary} onClick={onAdd}>{addLabel}</button>
        </div>
      ) : null}
      {items.length === 0 ? <div style={S.muted}>{emptyText}</div> : items.map(renderItem)}
    </>
  );
}
function PropertyRow({ property, defs }) {
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const addr = [property.addressLine1, property.city, property.state, property.zip].filter(Boolean).join(', ');
  async function toggle() {
    setShowHistory((v) => !v);
    if (history === null) {
      try { setHistory(await apiGet(`/customer/properties/${property.id}/history`)); } catch (e) { setHistory([]); }
    }
  }
  return (
    <div style={S.itemCard}>
      <strong>{property.label || addr || 'Property'}</strong>
      {property.propertyType && <span style={S.pill}>{property.propertyType}</span>}
      {addr && <div style={S.itemSub}>{addr}</div>}
      <CustomFieldsDisplay definitions={defs} values={property.customFields || {}} />
      <button style={S.linkBtn} onClick={toggle}>{showHistory ? 'Hide' : 'Service history'}</button>
      {showHistory && (
        history === null ? <div style={S.muted}>Loading…</div>
          : history.length === 0 ? <div style={S.muted}>No service history yet.</div>
          : history.map((h) => <div key={h.id} style={S.histRow}><span>{new Date(h.occurredAt).toLocaleDateString()}</span> · {h.subject || h.channel}</div>)
      )}
    </div>
  );
}

/* ---------- timeline + consent tabs ---------- */
function TimelineTab({ customerId, onLog }) {
  const [items, setItems] = useState(null);
  useEffect(() => { apiGet(`/customer/customers/${customerId}/timeline`).then(setItems).catch(() => setItems([])); }, [customerId]);
  return (
    <>
      {onLog ? <div style={{ textAlign: 'right', marginBottom: '0.75rem' }}><button style={S.btnPrimary} onClick={onLog}>+ Log Interaction</button></div> : null}
      {items === null ? <div style={S.muted}>Loading…</div>
        : items.length === 0 ? <div style={S.muted}>No activity yet.</div>
        : items.map((c) => (
          <div key={c.id} style={S.itemCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <strong style={{ textTransform: 'capitalize' }}>{c.channel}{c.direction ? ` · ${c.direction}` : ''}</strong>
              <span style={S.itemSub}>{new Date(c.occurredAt).toLocaleString()}</span>
            </div>
            {c.subject && <div>{c.subject}</div>}
            {c.body && <div style={S.itemSub}>{c.body}</div>}
          </div>
        ))}
      <ClassicSuiteEntries customerId={customerId} />
    </>
  );
}

/* ---- ClassicSuiteEntries({ customerId }) ------------------------
   WHY (2026-10-14, James via Fable; readiness 16): the classic look
   shows what the other Butlers did too (the clean look has the full
   list, SuiteTimeline.js). Same door, same words; one line each.  */
const CLASSIC_SOURCE_WORDS = { 'report-engine': 'Report Engine', dispatch: 'Dispatch', oncall: 'On-Call', water: 'Water test', schedule: 'Schedule' };

function ClassicSuiteEntries({ customerId }) {
  const [entries, setEntries] = useState(null); // null = loading

  useEffect(() => {
    apiGet(`/customer/timeline/customers/${customerId}`)
      .then((loadedEntries) => setEntries(Array.isArray(loadedEntries) ? loadedEntries : []))
      .catch(() => setEntries([]));
  }, [customerId]);

  return (
    <>
      <h4 style={{ margin: '1rem 0 0.5rem' }}>From the other Butlers</h4>
      {entries === null ? <div style={S.muted}>Loading…</div> : null}
      {entries !== null && entries.length === 0 ? <div style={S.muted}>Nothing from the other Butlers yet.</div> : null}
      {(entries || []).map((oneEntry) => (
        <div key={oneEntry.id} style={S.itemCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <strong>{oneEntry.linkUrl ? <a href={oneEntry.linkUrl} target="_blank" rel="noopener noreferrer">{oneEntry.summary}</a> : oneEntry.summary}</strong>
            <span style={S.itemSub}>{new Date(oneEntry.occurredAt).toLocaleString()}</span>
          </div>
          <div style={S.itemSub}>{CLASSIC_SOURCE_WORDS[oneEntry.sourceModule] || 'Another Butler'}</div>
        </div>
      ))}
    </>
  );
}
function ConsentTab({ customerId, onAdd }) {
  const [items, setItems] = useState(null);
  useEffect(() => { apiGet(`/customer/customers/${customerId}/consent`).then(setItems).catch(() => setItems([])); }, [customerId]);
  return (
    <>
      {onAdd ? <div style={{ textAlign: 'right', marginBottom: '0.75rem' }}><button style={S.btnPrimary} onClick={onAdd}>+ Record Consent</button></div> : null}
      {items === null ? <div style={S.muted}>Loading…</div>
        : items.length === 0 ? <div style={S.muted}>No consent records yet.</div>
        : items.map((r) => (
          <div key={r.id} style={S.itemCard}>
            <strong>{r.consentType.replace(/_/g, ' ')}</strong>
            <span style={{ ...S.pill, backgroundColor: r.status === 'granted' ? '#DCFCE7' : '#FEE2E2', color: r.status === 'granted' ? '#166534' : '#991B1B' }}>{r.status}</span>
            <div style={S.itemSub}>{new Date(r.occurredAt).toLocaleString()}{r.basis ? ` · ${r.basis}` : ''}</div>
          </div>
        ))}
    </>
  );
}

/* ---------- forms ---------- */
function FormShell({ onSubmit, saving, error, submitLabel, onCancel, children }) {
  return (
    <form onSubmit={onSubmit} style={{ padding: '1rem' }}>
      {error && <div style={S.error}>{error}</div>}
      {children}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
        <button type="button" style={S.btnSecondary} onClick={onCancel}>Cancel</button>
        <button type="submit" disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : submitLabel}</button>
      </div>
    </form>
  );
}
function useSaver(path, method) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  async function save(body, onSaved) {
    setSaving(true); setError(null);
    const r = await apiSend(path, method, body);
    setSaving(false);
    if (r.ok) onSaved(r.data); else setError((r.error && r.error.message) || 'Save failed');
  }
  return { saving, error, save };
}

function EditCustomerForm({ customer, defs, onCancel, onSaved }) {
  const [form, setForm] = useState({ firstName: customer.firstName || '', lastName: customer.lastName || '', companyName: customer.companyName || '', primaryEmail: customer.primaryEmail || '', primaryPhone: customer.primaryPhone || '', status: customer.status, source: customer.source || '' });
  const [cf, setCf] = useState(customer.customFields || {});
  const { saving, error, save } = useSaver(`/customer/customers/${customer.id}`, 'PUT');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <FormShell onSubmit={(e) => { e.preventDefault(); save({ ...form, customFields: cf }, onSaved); }} saving={saving} error={error} submitLabel="Save" onCancel={onCancel}>
      <div style={S.row}>
        {customer.type === 'company'
          ? <div style={S.field}><label style={S.label}>Company Name</label><input style={S.input} value={form.companyName} onChange={(e) => set('companyName', e.target.value)} /></div>
          : <>
              <div style={S.field}><label style={S.label}>First Name</label><input style={S.input} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} /></div>
              <div style={S.field}><label style={S.label}>Last Name</label><input style={S.input} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} /></div>
            </>}
      </div>
      <div style={S.row}>
        <div style={S.field}><label style={S.label}>Email</label><input style={S.input} type="email" value={form.primaryEmail} onChange={(e) => set('primaryEmail', e.target.value)} /></div>
        <div style={S.field}><label style={S.label}>Phone</label><input style={S.input} type="tel" value={form.primaryPhone} onChange={(e) => set('primaryPhone', e.target.value)} /></div>
      </div>
      <div style={S.row}>
        <div style={S.field}><label style={S.label}>Status</label>
          <select style={S.input} value={form.status} onChange={(e) => set('status', e.target.value)}>
            <option value="active">Active</option><option value="prospect">Prospect</option><option value="inactive">Inactive</option><option value="archived">Archived</option>
          </select>
        </div>
        <div style={S.field}><label style={S.label}>Source</label><input style={S.input} value={form.source} onChange={(e) => set('source', e.target.value)} /></div>
      </div>
      <CustomFieldsForm definitions={defs} values={cf} onChange={(k, v) => setCf((c) => ({ ...c, [k]: v }))} />
    </FormShell>
  );
}
function PropertyForm({ customerId, defs, onCancel, onSaved }) {
  const [form, setForm] = useState({ label: '', addressLine1: '', city: '', state: '', zip: '', propertyType: '' });
  const [cf, setCf] = useState({});
  const { saving, error, save } = useSaver('/customer/properties', 'POST');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <FormShell onSubmit={(e) => { e.preventDefault(); save({ ...form, customerId, customFields: cf }, onSaved); }} saving={saving} error={error} submitLabel="Add Property" onCancel={onCancel}>
      <div style={S.row}>
        <div style={S.field}><label style={S.label}>Label</label><input style={S.input} value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="Main house" /></div>
        <div style={S.field}><label style={S.label}>Type</label><input style={S.input} value={form.propertyType} onChange={(e) => set('propertyType', e.target.value)} placeholder="Residential" /></div>
      </div>
      <div style={S.field}><label style={S.label}>Address</label><input style={S.input} value={form.addressLine1} onChange={(e) => set('addressLine1', e.target.value)} /></div>
      <div style={S.row}>
        <div style={{ ...S.field, flex: 2 }}><label style={S.label}>City</label><input style={S.input} value={form.city} onChange={(e) => set('city', e.target.value)} /></div>
        <div style={S.field}><label style={S.label}>State</label><input style={S.input} maxLength={2} value={form.state} onChange={(e) => set('state', e.target.value.toUpperCase())} /></div>
        <div style={S.field}><label style={S.label}>Zip</label><input style={S.input} value={form.zip} onChange={(e) => set('zip', e.target.value)} /></div>
      </div>
      <CustomFieldsForm definitions={defs} values={cf} onChange={(k, v) => setCf((c) => ({ ...c, [k]: v }))} />
    </FormShell>
  );
}
function ContactForm({ customerId, onCancel, onSaved }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', role: '', email: '', phone: '', isPrimary: false });
  const { saving, error, save } = useSaver('/customer/contacts', 'POST');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <FormShell onSubmit={(e) => { e.preventDefault(); save({ ...form, customerId }, onSaved); }} saving={saving} error={error} submitLabel="Add Contact" onCancel={onCancel}>
      <div style={S.row}>
        <div style={S.field}><label style={S.label}>First Name</label><input style={S.input} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} /></div>
        <div style={S.field}><label style={S.label}>Last Name</label><input style={S.input} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} /></div>
      </div>
      <div style={S.row}>
        <div style={S.field}><label style={S.label}>Role</label><input style={S.input} value={form.role} onChange={(e) => set('role', e.target.value)} placeholder="Owner, tenant…" /></div>
        <div style={S.field}><label style={S.label}>Email</label><input style={S.input} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
        <div style={S.field}><label style={S.label}>Phone</label><input style={S.input} type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
        <input type="checkbox" checked={form.isPrimary} onChange={(e) => set('isPrimary', e.target.checked)} /> Primary contact
      </label>
    </FormShell>
  );
}
function CommForm({ customerId, onCancel, onSaved }) {
  const [form, setForm] = useState({ channel: 'call', direction: 'outbound', subject: '', body: '' });
  const { saving, error, save } = useSaver(`/customer/customers/${customerId}/communications`, 'POST');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <FormShell onSubmit={(e) => { e.preventDefault(); save(form, onSaved); }} saving={saving} error={error} submitLabel="Log" onCancel={onCancel}>
      <div style={S.row}>
        <div style={S.field}><label style={S.label}>Channel</label>
          <select style={S.input} value={form.channel} onChange={(e) => set('channel', e.target.value)}>{CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        </div>
        <div style={S.field}><label style={S.label}>Direction</label>
          <select style={S.input} value={form.direction} onChange={(e) => set('direction', e.target.value)}><option value="outbound">Outbound</option><option value="inbound">Inbound</option></select>
        </div>
      </div>
      <div style={S.field}><label style={S.label}>Subject</label><input style={S.input} value={form.subject} onChange={(e) => set('subject', e.target.value)} /></div>
      <div style={S.field}><label style={S.label}>Notes</label><textarea style={{ ...S.input, minHeight: '70px', resize: 'vertical' }} value={form.body} onChange={(e) => set('body', e.target.value)} /></div>
    </FormShell>
  );
}
function ConsentForm({ customerId, onCancel, onSaved }) {
  const [form, setForm] = useState({ consentType: 'marketing_email', status: 'granted', basis: '', source: '' });
  const { saving, error, save } = useSaver(`/customer/customers/${customerId}/consent`, 'POST');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <FormShell onSubmit={(e) => { e.preventDefault(); save(form, onSaved); }} saving={saving} error={error} submitLabel="Record" onCancel={onCancel}>
      <div style={S.row}>
        <div style={S.field}><label style={S.label}>Consent Type</label>
          <select style={S.input} value={form.consentType} onChange={(e) => set('consentType', e.target.value)}>{CONSENT_TYPES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}</select>
        </div>
        <div style={S.field}><label style={S.label}>Status</label>
          <select style={S.input} value={form.status} onChange={(e) => set('status', e.target.value)}><option value="granted">Granted</option><option value="withdrawn">Withdrawn</option></select>
        </div>
      </div>
      <div style={S.row}>
        <div style={S.field}><label style={S.label}>Basis</label><input style={S.input} value={form.basis} onChange={(e) => set('basis', e.target.value)} placeholder="express opt-in" /></div>
        <div style={S.field}><label style={S.label}>Source</label><input style={S.input} value={form.source} onChange={(e) => set('source', e.target.value)} placeholder="web form, phone…" /></div>
      </div>
    </FormShell>
  );
}

const S = {
  back: { background: 'none', border: 'none', color: 'var(--color-primary-600, #2563EB)', cursor: 'pointer', padding: '0.25rem 0', fontSize: '0.85rem', marginBottom: '0.5rem' },
  headerCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', padding: '1rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '10px', backgroundColor: 'var(--surface-card, #fff)', marginBottom: '1rem' },
  subline: { fontSize: '0.8rem', color: 'var(--text-secondary, #6B7280)', marginTop: '0.25rem' },
  tabs: { display: 'flex', gap: '0.25rem', flexWrap: 'wrap', borderBottom: '1px solid var(--color-border, #E5E7EB)', marginBottom: '1rem' },
  tab: { padding: '0.5rem 0.9rem', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-secondary, #6B7280)', borderBottom: '2px solid transparent' },
  tabActive: { color: 'var(--text-primary, #111)', fontWeight: 600, borderBottomColor: 'var(--color-primary-600, #2563EB)' },
  panel: { minHeight: '120px' },
  sectionTitle: { fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary, #6B7280)', marginBottom: '0.5rem' },
  itemCard: { padding: '0.75rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '8px', marginBottom: '0.5rem', backgroundColor: 'var(--surface-card, #fff)' },
  itemSub: { fontSize: '0.8rem', color: 'var(--text-secondary, #6B7280)', marginTop: '0.2rem' },
  pill: { marginLeft: '0.5rem', fontSize: '0.65rem', padding: '1px 6px', borderRadius: '9999px', backgroundColor: 'var(--surface-page, #F3F4F6)', color: 'var(--text-secondary, #6B7280)', textTransform: 'capitalize' },
  histRow: { fontSize: '0.8rem', color: 'var(--text-secondary, #444)', padding: '0.25rem 0', borderTop: '1px dashed var(--color-border, #E5E7EB)' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--color-primary-600, #2563EB)', cursor: 'pointer', padding: '0.4rem 0', fontSize: '0.8rem' },
  muted: { color: 'var(--text-secondary, #666)', fontSize: '0.875rem', padding: '0.5rem 0' },
  row: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  field: { flex: 1, minWidth: '130px', marginBottom: '0.5rem' },
  label: { display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: 'var(--text-secondary, #6B7280)' },
  input: { width: '100%', padding: '0.5rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '6px', fontSize: '0.875rem', backgroundColor: 'var(--surface-card, #fff)', color: 'var(--text-primary, #111)', boxSizing: 'border-box' },
  btnPrimary: { padding: '0.5rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, backgroundColor: 'var(--color-primary-600, #2563EB)', color: '#fff' },
  btnSecondary: { padding: '0.5rem 1rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '6px', backgroundColor: 'var(--surface-card, #fff)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 },
  error: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' },
};

export default CustomerDetailPage;
